'use client';

import { Dialog } from '@pixum/web-dialog';
import { DIALOG_STYLE, UPLOAD_ERROR_IMAGE_URL } from '../../../../constants';
import { openImageUploadPicker } from '../../../../utils';
import { getAcceptedFileFormats } from '@pixum/image-upload-service/lib/get-accepted-file-formats';
import type { UploadErrorBaseProps } from '../../../../types';
import { UploadErrorDialogContent } from '../UploadErrorDialogContent';
import styles from '../upload-error-dialog.module.css';
import { UploadErrorNoUploadReupload } from '../UploadErrorNoUploadReupload';
import { replacePlaceholderContent } from '../../utils/replacePlaceholderContent';

export function UploadErrorFileType({
  onClose,
  uploadErrors,
  uploadErrorStateActions,
  imageStateActions,
  constants,
  numOfFilesToBeUploaded
}: UploadErrorBaseProps) {
  const uuids = uploadErrors.map(e => e.uuid);
  const files = uploadErrors.map(e => e.file);
  const firstFileName = uploadErrors[0]?.file.name ?? '';
  const formats = getAcceptedFileFormats().map(f => f.toUpperCase());

  const handleClose = () => {
    uploadErrorStateActions.removeUploadErrors(uuids);
    imageStateActions.removeImages(uuids);
    onClose?.();
  };

  const handleReupload = () => {
    uploadErrorStateActions.removeUploadErrors(uuids);
    imageStateActions.removeImages(uuids);
    onClose?.();
    openImageUploadPicker();
  };

  const isMultiple = files.length > 1;
  const title = constants['i18n_upload_error_file_type_title'] ?? '';
  const titleMultiple = constants['i18n_upload_error_file_type_title_multiple'];
  const extensionReplacements = {
    '%allowedExtensionFirst%': formats[0] ?? '',
    '%allowedExtensionSecond%': formats[1] ?? '',
    '%allowedExtensionThird%': formats[2] ?? ''
  };
  const contentText = replacePlaceholderContent(constants['i18n_upload_error_image_file_type_content'], {
    '%filename%': firstFileName,
    ...extensionReplacements
  });
  const contentTextMultiple = replacePlaceholderContent(constants['i18n_upload_error_image_file_type_content_multiple'], {
    '%numErrorFiles%': String(uploadErrors.length),
    '%numUploadFiles%': String(numOfFilesToBeUploaded),
    ...extensionReplacements
  });
  const affectedFilesLabel = replacePlaceholderContent(constants['i18n_upload_affected_images'], {
    '%numErrorFiles%': String(uploadErrors.length)
  });

  return (
    <Dialog
      open
      className={styles.dialog}
      onClose={handleClose}
      showCloseButton={false}
      style={DIALOG_STYLE}
      content={
        <UploadErrorDialogContent
          imageUrl={UPLOAD_ERROR_IMAGE_URL.UNKNOWN_FORMAT}
          title={title}
          titleMultiple={titleMultiple}
          contentText={contentText}
          contentTextMultiple={contentTextMultiple}
          files={files}
          showFileList={isMultiple}
          affectedFilesLabel={affectedFilesLabel}
        />
      }
      footer={
        <UploadErrorNoUploadReupload constants={constants} onDontUpload={handleClose} onReupload={handleReupload} />
      }
    />
  );
}
